import { Popover, Transition } from "@headlessui/react";
import { Fragment, useEffect, useRef, useState } from "react";
import cn from "classnames";
import Button from "@elements/button";
import Image from "next/legacy/image";
import { CartItemModel, CartStep, IWrappingInput, IWrappingModel } from "@types";
import { useAppContext } from "@context/app";
import { useTranslations } from "next-intl";
import { productApi } from "gate";
import Toggle from "@elements/toggle";
import RadioGroup from "@elements/fields/radioGroup";
import { FieldArray, Form, Formik, FormikErrors, FormikHelpers, FormikProps, FormikTouched } from "formik";
import * as Yup from "yup";
import TextInput from "@elements/fields/textInput";
import GiftIcon from "@svg/gift";
import TrashIcon from "@svg/trash";
import { useRouter } from "next/router";
import ImagePrepare from "@elements/imagePrepare";
import useRequestImmutable from "@hooks/useRequestImmutable";
import Modal from "@modules/layout/modal";
import WrappingDonation from "@modules/donation/WrappingDonation";
import BalloonInstancePicker from "./balloons/BalloonInstancePicker";
import {
  balloonsMapToModels,
  balloonsToCountMap,
  isBalloonMapDirty,
  isBalloonMapsDirty,
} from "./newGiftOptionsUtils";

interface Props {
  className?: string;
  item: CartItemModel;
  action?: (value: {
    next?: CartStep;
    current: CartStep;
    goCheckout?: boolean;
  }) => void;
}

interface UserGiftOption {
  wrapping?: IWrappingInput;
  cardDesign?: IWrappingInput;
  from: string;
  to: string;
  message: string;
}

const GiftOptions: React.FC<Props> = ({ className, item, action }) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [showWrappingDonationModal, setShowWrappingDonationModal] = useState(false)
  const t = useTranslations();
  const { locale } = useRouter();
  let qtyArray = Array.from({ length: item.qty }, (_, index) => index);
  const { setUserCart, cart, syncData } = useAppContext();

  const sameFormRef = useRef<FormikProps<any>>(null);
  const diffFormRef = useRef<FormikProps<any>>(null);

  const [wrappingList, setWrappingList] = useState<IWrappingInput[]>([]);
  const [giftCardList, setGiftCardList] = useState<IWrappingInput[]>([]);
  const [isWrappable, setIsWrappable] = useState(true);
  const [differentOptions, setDifferentOption] = useState(false);
  const [cardCount, setCardCount] = useState<number>(0);
  const [addonItems, setAddonItems] = useState<IWrappingModel[]>([]);
  const [balloonLimit, setBalloonLimit] = useState<number>(0);
  const [balloonList, setBalloonList] = useState<IWrappingInput[]>([]);
  const [sameBalloons, setSameBalloons] = useState<Record<string, number>>({});
  const [diffBalloons, setDiffBalloons] = useState<Record<string, number>[]>([]);
  const [initialSameBalloons, setInitialSameBalloons] = useState<Record<string, number>>({});
  const [initialDiffBalloons, setInitialDiffBalloons] = useState<Record<string, number>[]>([]);
  const [sameGiftOption] = useState<UserGiftOption>({
    wrapping: undefined,
    cardDesign: undefined,
    from: "",
    to: "",
    message: "",
  });
  const [diffGiftOption, setDiffGiftOptions] = useState<{
    userGifts: UserGiftOption[];
  }>({ userGifts: [] });

  const { data } = useRequestImmutable(productApi.wrappingList({ id: Number(item.id), count: item.qty }), { shouldRetryOnError: false, revalidateOnMount: true })
  const isGiftCardEnabled = syncData?.app_settings?.allow_giftcard === true;
  const hasGiftCards = isGiftCardEnabled && giftCardList.length > 0;

  const getGiftCardDesign = (
    card?: {
      id?: string;
      name?: string;
      price?: IWrappingInput["price"];
      media?: IWrappingInput["media"];
    } | null
  ): IWrappingInput | undefined => {
    if (!hasGiftCards || !card?.id) {
      return undefined;
    }
    const target = giftCardList.find((giftCard) => String(giftCard.id) === String(card.id));
    if (target) {
      return target;
    }
    if (card.name && card.price && card.media) {
      const parsedId = Number(card.id);
      if (!Number.isNaN(parsedId)) {
        return {
          id: parsedId,
          name: card.name,
          price: card.price,
          media: card.media,
        };
      }
    }
    return undefined;
  };

  const buildCardPayload = (gift?: UserGiftOption) => {
    if (!hasGiftCards || !gift?.cardDesign) {
      return null;
    }
    return {
      id: String(gift.cardDesign.id),
      name: gift.cardDesign.name,
      price: gift.cardDesign.price,
      media: gift.cardDesign.media,
      from: gift.from?.trim() ? gift.from : undefined,
      to: gift.to?.trim() ? gift.to : undefined,
      message: gift.message?.trim() ? gift.message : undefined,
    };
  };

  useEffect(() => {
    if (data) {
      setIsWrappable(data.data.wrappable);
      if (data.data.wrappable) {
        setWrappingList(data.data.items[0]?.wrappings);
      }
      setGiftCardList(Array.isArray(data.data.gift_cards) ? data.data.gift_cards : []);
      setBalloonLimit(data.data.balloon_limit || 0);
      // API returns balloons grouped, pick the first group for now.
      setBalloonList(data.data.balloonsItems[0]?.balloons || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (item.gifts.length) {
      setDifferentOption(true);
    }
  }, [item]);

  useEffect(() => {
    const addonList: IWrappingModel[] = [];
    let _c = 0;
    const list: UserGiftOption[] = [];
    const balloonsMap: Record<string, number>[] = [];
    for (const g of item.gifts) {
      const selectedCard = getGiftCardDesign(g.card);
      list.push({
        wrapping: g.wrapping || undefined,
        cardDesign: selectedCard,
        from: hasGiftCards ? g.card?.from || "" : "",
        to: hasGiftCards ? g.card?.to || "" : "",
        message: hasGiftCards ? g.card?.message || "" : "",
      });
      balloonsMap.push(balloonsToCountMap(g.balloons));
      if (g.wrapping) {
        addonList.push(g.wrapping);
      }
      if (Array.isArray(g.balloons)) {
        for (const balloon of g.balloons) {
          const count = Math.max(0, Math.floor(Number(balloon?.count) || 0));
          for (let i = 0; i < count; i += 1) {
            addonList.push(balloon);
          }
        }
      }
      if (hasGiftCards && g.card?.id) {
        _c += 1;
        if (selectedCard) {
          addonList.push(selectedCard);
        }
      }
    }
    setCardCount(_c);
    setDiffGiftOptions({ userGifts: [...list] });
    setDiffBalloons(balloonsMap);
    setSameBalloons(balloonsMap[0] || {});
    setInitialDiffBalloons(balloonsMap);
    setInitialSameBalloons(balloonsMap[0] || {});
    setAddonItems(addonList);
  }, [item, giftCardList, hasGiftCards]);

  // Formik's "dirty" doesn't track our custom balloon state, so we track it manually.
  const isSameBalloonsDirty = isBalloonMapDirty(sameBalloons, initialSameBalloons);
  const isDiffBalloonsDirty = isBalloonMapsDirty(diffBalloons, initialDiffBalloons, item.qty);
  const hasWrapping = isWrappable && wrappingList.length > 0;
  const hasBalloons = balloonList.length > 0;
  const allowDifferentOptions = item.qty > 1 && (hasWrapping || hasBalloons || hasGiftCards);
  const useDifferentGiftForm = differentOptions || (!hasWrapping && !hasBalloons && !hasGiftCards);

  const goCheckout = () => {
    action?.({ current: "gift", goCheckout: true });
  };

  const optionalGiftMessageField = (minLength: number) =>
    Yup.string().test("min", (t("client_errors_minchar")).replace("@", String(minLength)), (value) => {
      const trimmed = value?.trim();
      if (!trimmed) return true;
      return trimmed.length >= minLength;
    });

  const SameGiftOptionsSchema = Yup.object().shape({
    cardDesign: Yup.mixed().test("card-design-required", t("client_errors_fieldisrequiered"), (value, context) => {
      if (!hasGiftCards) {
        return true;
      }
      if ((context.parent.from || context.parent.to || context.parent.message) && !value) {
        return false;
      }
      return true;
    }),
    from: optionalGiftMessageField(2),
    to: optionalGiftMessageField(2),
    message: optionalGiftMessageField(3),
  });

  const DiffGiftOptionsSchema = Yup.object().shape({
    userGifts: Yup.array().of(
      Yup.object().shape({
        cardDesign: Yup.mixed().test("card-design-required", t("client_errors_fieldisrequiered"), (value, context) => {
          if (!hasGiftCards) {
            return true;
          }
          if ((context.parent.from || context.parent.to || context.parent.message) && !value) {
            return false;
          }
          return true;
        }),
        from: optionalGiftMessageField(2),
        to: optionalGiftMessageField(2),
        message: optionalGiftMessageField(3),
      })
    ),
  });

  const onSubmit = (
    value: UserGiftOption[],
    actions: FormikHelpers<any>,
    close: () => void
  ) => {
    const _cart = [...cart];
    if (useDifferentGiftForm) {
      for (const iterator of _cart) {
        if (iterator.id === item.id) {
          for (const [idx] of qtyArray.entries()) {
            const gift = value[idx];
            iterator.gifts[idx] = {
              wrapping: gift?.wrapping ?? null,
              card: buildCardPayload(gift),
              balloons: balloonsMapToModels(diffBalloons[idx] || {}, balloonList),
            };
          }
        }
      }
      setUserCart([..._cart]);
      setIsPopoverOpen(false);
      close();
    } else {
      for (const iterator of _cart) {
        if (iterator.id === item.id) {
          for (const [idx] of qtyArray.entries()) {
            if (value[0]) {
              iterator.gifts[idx] = {
                wrapping: value[0].wrapping ?? null,
                card: buildCardPayload(value[0]),
                balloons: balloonsMapToModels(sameBalloons, balloonList),
              };
            } else {
              iterator.gifts[idx] = { wrapping: null, card: null, balloons: [] as any };
            }
          }
        }
      }
      setUserCart([..._cart]);
      setIsPopoverOpen(false);
      close();
    }
  };

  const handelSetDifferentOption = (value: boolean) => {
    setDifferentOption(value);
    if (value) {
      const _data = { ...sameFormRef.current?.values }
      diffFormRef.current?.setFieldValue('userGifts[0].wrapping', _data?.wrapping)
      diffFormRef.current?.setFieldTouched('userGifts[0].wrapping', true)
      diffFormRef.current?.setFieldValue('userGifts[0].cardDesign', _data?.cardDesign)
      diffFormRef.current?.setFieldTouched('userGifts[0].cardDesign', true)
      setDiffBalloons((prev) => {
        const next = [...(prev || [])];
        next[0] = { ...(sameBalloons || {}) };
        return next;
      });
    } else {
      const _data = { ...diffFormRef.current?.values?.userGifts[0] }
      sameFormRef.current?.setFieldValue('wrapping', _data?.wrapping)
      sameFormRef.current?.setFieldTouched('wrapping', true)
      sameFormRef.current?.setFieldValue('cardDesign', _data?.cardDesign)
      sameFormRef.current?.setFieldTouched('cardDesign', true)
      setSameBalloons({ ...(diffBalloons?.[0] || {}) });
    }
  };

  const deleteGift = () => {
    const _cart = [...cart];
    for (const iterator of _cart) {
      if (iterator.id === item.id) {
        iterator.gifts = []
      }
    }
    sameFormRef.current?.resetForm()
    diffFormRef.current?.resetForm()
    setDifferentOption(false)
    setSameBalloons({})
    setDiffBalloons([])
    setUserCart([..._cart]);
  }

  return (
    <Popover className={cn("relative md:max-w-[270px] max-w-full md:w-max w-full min-w-[200px]", className)} as="nav">
      {({ close }) => (
        <>
          <Transition
            as={Fragment}
            show={isPopoverOpen}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 backdrop-brightness-50 z-[60]"
              onClick={() => {
                setIsPopoverOpen(false)
              }}
            ></div>
          </Transition>
          <button className="text-gray-500 z-[20] relative focus:outline-none w-full"
            onClick={() => {
              (!syncData?.allow_donation || addonItems.length || cardCount) ?
                setIsPopoverOpen(true) :
                setShowWrappingDonationModal(true)
            }}>
            <div className={`  text-[#008AF6] w-full flex justify-center`}>
              {addonItems.length || cardCount ? (
                <div className="flex  py-2  rounded-lg items-center space-x-5 justify-between w-full">
                  <div className="flex-1 flex items-center">
                    {addonItems.slice(0, 2).map((addon, idx) => (
                      <div key={`${addon?.id}-${idx}`}>
                        <div className=" w-12 h-12 relative rounded overflow-hidden ml-1">
                          <ImagePrepare media={addon.media} alt={addon.name || "dabdoob"} width={48} />
                        </div>
                      </div>
                    ))}
                    {addonItems.length > 2 && (
                      <div className=" w-12 h-12 bg-[#F5F5F5] relative rounded overflow-hidden ml-1 flex justify-center items-center">
                        <p className="m-auto text-[#333] font-normal text-xs">{`+${addonItems.length - 2
                        }`}</p>
                      </div>
                    )}
                    {cardCount ? (
                      <div className=" flex flex-col ml-1 items-center">
                        <p className=" text-[#333] text-xs font-normal ">
                          {t("client_giftcard_giftcard")}
                        </p>
                        <p className=" text-[10px] text-[#999] font-normal">{`${cardCount} ${cardCount > 1 ? t("client_giftoptions_cards") : t("client_giftoptions_card")}`}</p>
                      </div>
                    ) : null}
                  </div>

                  <div className={`w-10 h-10 border rounded-lg flex items-center justify-center ${locale?.includes("ar") ? "mr-2" : 'ml-2'} `} onClick={(e) => { e.stopPropagation(); deleteGift() }}>
                    <TrashIcon />
                  </div>
                </div>
              ) : (
                <div className="flex md:border  md:py-2 p-3 md:px-5 rounded-full items-center  w-full md:mb-3 justify-center">
                  <GiftIcon className=" cursor-pointer mx-1" />
                  <p className=" font-normal px-1 text-sm whitespace-nowrap">
                    {t("client_giftoptions_wrapping")}
                  </p>
                </div>
              )}
            </div>
          </button>

          <Transition
            as={Fragment}
            show={isPopoverOpen}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 -translate-x-full"
            enterTo="opacity-100 -translate-x-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 -translate-x-0"
            leaveTo="opacity-0 -translate-x-full"
          >
            <Popover.Panel className=" flex flex-col justify-between fixed height-screen top-[0px] z-[60] left-0  w-screen overflow-hidden shadow-lg ring-1 ring-black ring-opacity-5 md:max-w-[450px] xs:max-w-xs max-w-xs bg-white transform ">
              <div className="w-full pt-6 h-[calc(100%-90px)] overflow-auto">
                <p className=" font-bold text-xl px-3">{t("client_checkout_customizations")}</p>
                {allowDifferentOptions ? (
                  <div className="w-full py-5 px-3 flex justify-between items-center">
                    <p className=" text-[#999] font-normal text-base ">
                      {t("client_giftoptions_differentoptions")}
                    </p>
                    <Toggle
                      value={differentOptions}
                      action={(e) => handelSetDifferentOption(e)}
                    />
                  </div>
                ) : null}
                <div className="w-full flex flex-wrap  mt-5 px-3">
                  <div className="relative w-32 h-32 rounded-xl overflow-hidden">
                    <Image
                      src={item.image}
                      layout="fill"
                      quality={100}
                      alt=""
                    />
                  </div>
                  <div className="flex-1 px-3 py-3 flex flex-col justify-between">
                    <p className="w-full text-base font-normal text-[#333]">
                      {item.productName}
                    </p>
                    <p className="w-full text-base font-normal text-[#999] mt-4 mb-2">{`${item.qty
                      } ${item.qty > 1 ? t("client_giftoptions_items") : t("client_giftoptions_item")}`}</p>
                  </div>
                </div>
                <div className={cn("w-full  flex-wrap mt-10", !useDifferentGiftForm ? "flex" : "hidden")}>
                  <Formik
                    enableReinitialize
                    innerRef={sameFormRef}
                    initialValues={sameGiftOption}
                    validationSchema={() => SameGiftOptionsSchema}
                    validateOnBlur={false}
                    onSubmit={(values, actions) => {
                      onSubmit([values], actions, close);
                    }}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur,
                      setFieldValue,
                      setFieldTouched,
                      isSubmitting,
                      isValid,
                      dirty,
                    }) => (
                      <Form className="flex flex-wrap w-full m-auto form">
                        {wrappingList.length && isWrappable ? (
                          <div className=" mb-5 w-full px-2">
                            <p className="w-full mb-4 text-base font-normal text-[#333]">
                              {t("client_giftoptions_wrappingpaper")}
                            </p>
                            <RadioGroup
                              fieldName="wrapping"
                              options={wrappingList}
                              selected={values?.wrapping}
                              name="wrapping"
                              onChange={(v) => setFieldValue("wrapping", v)}
                            />
                          </div>
                        ) : null}
                        {balloonLimit > 0 && balloonList.length ? (
                          <div className="w-full mb-8 px-2">
                            <div className="w-full flex items-end gap-2">
                              <p className="text-base font-normal text-[#333]">
                                {t("client_giftoptions_helium_balloons")}
                              </p>
                              <p className="text-xs text-[#999]">
                                {t("client_giftoptions_optional")}
                              </p>
                            </div>
                            <p className="text-sm text-[#999] mt-1">
                              {t("client_giftoptions_balloon_limit").replace(
                                "@",
                                String(balloonLimit)
                              )}
                            </p>
                            <div className="mt-4">
                              {/* If "different options" is OFF, apply the same per-instance limit to all. */}
                              <BalloonInstancePicker
                                balloons={balloonList}
                                limit={balloonLimit}
                                value={sameBalloons}
                                onChange={setSameBalloons}
                              />
                            </div>
                          </div>
                        ) : null}
                        {hasGiftCards ? (
                          <div className="mb-8 w-full px-2">
                            <p className="w-full mb-4 text-base font-normal text-[#333]">
                              {t("client_giftoptions_giftcard")}
                            </p>
                            <RadioGroup
                              fieldName="cardDesign"
                              options={giftCardList}
                              selected={values?.cardDesign}
                              name="cardDesign"
                              onChange={(v) => {
                                setFieldValue("cardDesign", v);
                                setFieldTouched("cardDesign", true, false);
                              }}
                              error={typeof errors.cardDesign === "string" ? errors.cardDesign : ""}
                            />
                          </div>
                        ) : null}
                        {hasGiftCards ? (
                          <div className="w-full mb-5 px-2">
                            <div className="w-full mb-3 flex items-end gap-2">
                              <p className="text-base font-normal text-[#333]">{t("client_giftoptions_message")}</p>
                              <p className="text-xs text-[#999]">{t("client_giftoptions_optional")}</p>
                            </div>
                            <div className=" w-full flex flex-wrap">
                              <div className="w-1/2 p-1">
                                <TextInput
                                  name="from"
                                  value={values?.from}
                                  error={errors.from && touched.from ? errors.from : ""}
                                  label={t("client_giftoptions_from")}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                              </div>
                              <div className="w-1/2 p-1">
                                <TextInput
                                  name="to"
                                  value={values?.to}
                                  error={
                                    errors.to && touched.to
                                      ? errors.to
                                      : ""
                                  }
                                  label={t("client_giftoptions_to")}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                              </div>
                              <div className="w-full p-1">
                                <TextInput
                                  name="message"
                                  value={values?.message}
                                  error={
                                    errors.message && touched.message
                                      ? errors.message
                                      : ""
                                  }
                                  label={t("client_giftoptions_message")}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                              </div>
                            </div>
                          </div>
                        ) : null}
                        <div className="w-full flex flex-wrap justify-between items-center h-20 fixed bottom-0 rounded-t-[16px] shadow-[0px_-8px_32px_rgba(0,0,0,0.12)] p-3">
                          <Button
                            type="submit"
                            isDisabled={!isValid || (!dirty && !isSameBalloonsDirty)}
                            className="w-full h-12 font-extrabold text-base rounded-full"
                            size="slim"
                          >
                            {t("client_buttons_save")}
                          </Button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>
                <Formik
                  enableReinitialize
                  innerRef={diffFormRef}
                  initialValues={diffGiftOption}
                  validationSchema={() => DiffGiftOptionsSchema}
                  validateOnBlur={false}
                  onSubmit={(values, actions) => {
                    onSubmit(values.userGifts, actions, close);
                  }}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    setFieldValue,
                    setFieldTouched,
                    isSubmitting,
                    isValid,
                    dirty,
                  }) => (
                    <Form className={cn("flex-wrap w-full m-auto form", useDifferentGiftForm ? "flex" : "hidden")}>
                      <FieldArray
                        name="userGifts"
                        render={(arrayHelpers) => {
                          return (
                            <div className="w-full">
                              {qtyArray.map((q, index) => (
                                <div key={`${q}`} className="w-full">
                                  <p className="w-full mb-4 text-base font-normal text-[#333] mt-10 px-2">{`${t(
                                    "w-wrappingPaper"
                                  )} ${qtyArray.length > 1 ? index + 1 : ""
                                    }`}</p>
                                  {!wrappingList.length ? (
                                    <p className=" text-base font-normal text-[#7A3A00] text-center w-full m-auto bg-[#FFF7DF] rounded-2xl p-4 mt-1 mb-5 ">
                                      {t("client_giftoptons_nowrappinghint")}
                                    </p>
                                  ) : null}
                                  {wrappingList.length && isWrappable ? (
                                    <div className=" mb-5 w-full px-2">
                                      <RadioGroup
                                        id={`${index}-wrapping`}
                                        fieldName="wrapping"
                                        options={wrappingList}
                                        selected={
                                          values.userGifts[index]?.wrapping
                                        }
                                        name={`userGifts.${index}.wrapping`}
                                        onChange={(v) =>
                                          setFieldValue(
                                            `userGifts.${index}.wrapping`,
                                            v
                                          )
                                        }
                                      />
                                    </div>
                                  ) : null}
                                  {balloonLimit > 0 && balloonList.length ? (
                                    <div className="w-full mb-8 px-2">
                                      <div className="w-full flex items-end gap-1">
                                        <p className="text-base font-normal text-[#333]">
                                          {t("client_giftoptions_helium_balloons")}
                                        </p>
                                        <p className="text-xs text-[#999]">
                                          {t("client_giftoptions_optional")}
                                        </p>
                                      </div>
                                      <p className="text-sm text-[#999] mt-1">
                                        {t("client_giftoptions_balloon_limit").replace(
                                          "@",
                                          String(balloonLimit)
                                        )}
                                      </p>
                                      <div className="mt-4">
                                        {/* With "different options" ON, each instance can use the full limit. */}
                                        <BalloonInstancePicker
                                          balloons={balloonList}
                                          limit={balloonLimit}
                                          value={diffBalloons[index] || {}}
                                          onChange={(next) =>
                                            setDiffBalloons((prev) => {
                                              const arr = [...(prev || [])];
                                              arr[index] = next;
                                              return arr;
                                            })
                                          }
                                        />
                                      </div>
                                    </div>
                                  ) : null}
                                  {hasGiftCards ? (
                                    <div className="mb-8 w-full px-2">
                                      <p className="w-full mb-4 text-base font-normal text-[#333]">
                                        {t("client_giftoptions_giftcard")}
                                      </p>
                                      <RadioGroup
                                        id={`${index}-card-design`}
                                        fieldName="cardDesign"
                                        options={giftCardList}
                                        selected={values.userGifts[index]?.cardDesign}
                                        name={`userGifts.${index}.cardDesign`}
                                        onChange={(v) => {
                                          setFieldValue(`userGifts.${index}.cardDesign`, v);
                                          setFieldTouched(`userGifts.${index}.cardDesign`, true, false);
                                        }}
                                        error={
                                          errors.userGifts &&
                                          typeof (errors.userGifts as FormikErrors<any>[])[index]?.cardDesign === "string"
                                            ? (errors.userGifts as FormikErrors<any>[])[index]?.cardDesign as string
                                            : ""
                                        }
                                      />
                                    </div>
                                  ) : null}
                                  {hasGiftCards ? (
                                    <div className="w-full mb-5 px-2">
                                      <div className="w-full mb-3 flex items-end gap-2">
                                        <p className="text-base font-normal text-[#333]">{`${t(
                                          "client_giftoptions_message"
                                        )} ${qtyArray.length > 1 ? index + 1 : ""}`}</p>
                                        <p className="text-xs text-[#999]">{t("client_giftoptions_optional")}</p>
                                      </div>
                                      <div className=" w-full flex flex-wrap">
                                        <div className="w-1/2 p-1">
                                          <TextInput
                                            name={`userGifts.${index}.from`}
                                            value={
                                              values.userGifts[index]?.from
                                            }
                                            error={
                                              (touched.userGifts &&
                                                (touched.userGifts as FormikTouched<any>[])[index]?.from &&
                                              errors.userGifts &&
                                                (errors.userGifts as FormikErrors<any>[])[index]?.from) ?
                                                (errors.userGifts as FormikErrors<any>[])[index].from : undefined
                                            }
                                            label={t("client_giftoptions_from")}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                          />
                                        </div>
                                        <div className="w-1/2 p-1">
                                          <TextInput
                                            name={`userGifts.${index}.to`}
                                            value={values.userGifts[index]?.to}
                                            error={
                                              (touched.userGifts &&
                                                (touched.userGifts as FormikTouched<any>[])[index]?.to &&
                                              errors.userGifts &&
                                                (errors.userGifts as FormikErrors<any>[])[index]?.to) ?
                                                (errors.userGifts as FormikErrors<any>[])[index].to : undefined
                                            }
                                            label={t("client_giftoptions_to")}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                          />
                                        </div>
                                        <div className="w-full p-1">
                                          <TextInput
                                            name={`userGifts.${index}.message`}
                                            value={
                                              values.userGifts[index]?.message
                                            }
                                            error={
                                              (touched.userGifts &&
                                                (touched.userGifts as FormikTouched<any>[])[index]?.message &&
                                              errors.userGifts &&
                                                (errors.userGifts as FormikErrors<any>[])[index]?.message) ?
                                                (errors.userGifts as FormikErrors<any>[])[index].message : undefined
                                            }
                                            label={t("client_giftoptions_message")}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  ) : null}
                                </div>
                              ))}
                            </div>
                          );
                        }}
                      />
                      <div className="w-full flex flex-wrap justify-between items-center h-20 fixed bottom-0 rounded-t-[16px] shadow-[0px_-8px_32px_rgba(0,0,0,0.12)] p-3">
                        <Button
                          type="submit"
                          isDisabled={!isValid || (!dirty && !isDiffBalloonsDirty)}
                          className="w-full  font-extrabold text-base rounded-full"
                          size="slim"
                        >
                          {t("client_buttons_save")}
                        </Button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </Popover.Panel>
          </Transition>
          <Modal
            cancelable
            onClose={() => setShowWrappingDonationModal(false)}
            open={showWrappingDonationModal}
          >
            <div className="w-screen md:max-w-[500px] min-h-[363px] bg-white rounded-lg p-4 md:w-screen">
              <WrappingDonation
                onConfirm={() => {
                  goCheckout();
                  setShowWrappingDonationModal(false)
                }}
                onCancel={() => {
                  setShowWrappingDonationModal(false)
                  setIsPopoverOpen(true)
                }}
              />
            </div>
          </Modal>
        </>
      )}
    </Popover>
  );
};

export default GiftOptions;
